import { NewsContext, FilteredNewsItem } from '@/types/news';

// Cache configuration
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
const CACHE_KEY_PREFIX = 'news_cache_';

interface CacheEntry {
  data: NewsContext;
  timestamp: number;
  expiresAt: number;
}

// Generate cache key based on parameters
export function generateCacheKey(params: Record<string, any> = {}): string {
  const sortedParams = Object.keys(params)
    .sort()
    .map(key => `${key}=${params[key]}`)
    .join('&');
  return `${CACHE_KEY_PREFIX}${sortedParams}`;
}

// Check if cache entry is valid
export function isCacheValid(entry: CacheEntry): boolean {
  return Date.now() < entry.expiresAt;
}

// Get cached news data
export function getCachedNews(cacheKey: string): NewsContext | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const entry: CacheEntry = JSON.parse(cached);
    
    if (isCacheValid(entry)) {
      return entry.data;
    }

    // Remove expired cache
    localStorage.removeItem(cacheKey);
    return null;
  } catch (error) {
    console.warn('Failed to get cached news:', error);
    return null;
  }
}

// Cache news data
export function cacheNews(cacheKey: string, data: NewsContext): void {
  try {
    if (typeof localStorage === 'undefined') return;

    const entry: CacheEntry = {
      data,
      timestamp: Date.now(),
      expiresAt: Date.now() + CACHE_DURATION
    };

    localStorage.setItem(cacheKey, JSON.stringify(entry));
  } catch (error) {
    console.warn('Failed to cache news:', error);
  }
}

// Clear all news cache
export function clearNewsCache(): void {
  try {
    if (typeof localStorage === 'undefined') return;

    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Failed to clear news cache:', error);
  }
}

// Clean expired cache entries
export function cleanExpiredCache(): void {
  try {
    if (typeof localStorage === 'undefined') return;

    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            const entry: CacheEntry = JSON.parse(cached);
            if (!isCacheValid(entry)) {
              localStorage.removeItem(key);
            }
          }
        } catch (error) {
          // Remove corrupted cache entries
          localStorage.removeItem(key);
        }
      }
    });
  } catch (error) {
    console.warn('Failed to clean expired cache:', error);
  }
}

// Get cache statistics
export function getCacheStats(): {
  totalEntries: number;
  validEntries: number;
  expiredEntries: number;
  totalSize: number;
} {
  const stats = {
    totalEntries: 0,
    validEntries: 0,
    expiredEntries: 0,
    totalSize: 0
  };

  try {
    if (typeof localStorage === 'undefined') return stats;

    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(CACHE_KEY_PREFIX)) {
        stats.totalEntries++;
        
        try {
          const cached = localStorage.getItem(key);
          if (cached) {
            stats.totalSize += cached.length;
            const entry: CacheEntry = JSON.parse(cached);
            
            if (isCacheValid(entry)) {
              stats.validEntries++;
            } else {
              stats.expiredEntries++;
            }
          }
        } catch (error) {
          stats.expiredEntries++;
        }
      }
    });
  } catch (error) {
    console.warn('Failed to get cache stats:', error);
  }

  return stats;
}